import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Check, Loader2 } from "lucide-react";
import "./CouchageSurvey.css";

const API = process.env.REACT_APP_BACKEND_URL;

// Photos définitives fournies par l'admin (jamais générées).
const IMG_LIT_PARAPLUIE = "https://customer-assets-gfyr7b9c.emergentagent.net/job_curieux-hub/artifacts/sm796moh_446D0012-338A-44FB-93FB-04FAAF156D52.png";
const IMG_CANAPE_DEPLOYE = "https://customer-assets-gfyr7b9c.emergentagent.net/job_curieux-hub/artifacts/3tp723ax_A97FCFE9-6B5D-444C-8CA8-B7C542F6A177.png";
const IMG_CANAPE_PLIE = "https://customer-assets-gfyr7b9c.emergentagent.net/job_curieux-hub/artifacts/e2nov3f3_IMG_8720.jpeg";

const CHOICE_SUMMARIES = {
  lit_parapluie: ["Choix couchage — 3e voyageur enfant", "✓ Lit parapluie", "✓ Linge de maison fourni"],
  canape_deploye: ["Choix couchage — 3e voyageur enfant", "✓ Canapé dépliant", "✓ Déployé à l'arrivée", "✓ Linge de maison fourni"],
  canape_plie: ["Choix couchage — 3e voyageur enfant", "✓ Canapé dépliant", "✓ Plié à l'arrivée", "✓ Linge de maison fourni"],
};

function buildSummary(choice) {
  return (CHOICE_SUMMARIES[choice] || []).join("\n");
}

/**
 * CouchageFormulaire — page publique du choix de couchage pour le 3ᵉ voyageur.
 *   • Lien sécurisé par token (/couchage/:token) → la réponse se rattache
 *     automatiquement à la bonne réservation (POST /api/rg/public/couchage/:token).
 *   • URL statique de secours (/formulaire-couchage, sans token) → le voyageur
 *     envoie son résumé à l'hôte via WhatsApp (numéro configuré dans Réglages).
 */
export default function CouchageFormulaire() {
  const { token } = useParams();
  const [adminPhone, setAdminPhone] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [mainChoice, setMainChoice] = useState(null); // "lit_parapluie" | "canape"
  const [canapeSub, setCanapeSub] = useState(null);   // "deploye" | "plie"
  const [done, setDone] = useState(null); // { summary, attached }

  useEffect(() => {
    (async () => {
      try {
        if (token) {
          const r = await fetch(`${API}/api/rg/public/couchage/${token}`);
          if (!r.ok) { setNotFound(true); return; }
          const data = await r.json();
          const ex = data.existing;
          if (ex === "lit_parapluie") { setMainChoice("lit_parapluie"); }
          else if (ex === "canape_deploye") { setMainChoice("canape"); setCanapeSub("deploye"); }
          else if (ex === "canape_plie") { setMainChoice("canape"); setCanapeSub("plie"); }
        } else {
          const r = await fetch(`${API}/api/rg/public/couchage-config`);
          const data = await r.json();
          setAdminPhone((data.admin_phone || "").trim());
        }
      } catch { if (token) setNotFound(true); }
      finally { setLoading(false); }
    })();
  }, [token]);

  const canValidate =
    mainChoice === "lit_parapluie" ||
    (mainChoice === "canape" && (canapeSub === "deploye" || canapeSub === "plie"));

  const finalChoice = () => {
    if (mainChoice === "lit_parapluie") return "lit_parapluie";
    if (mainChoice === "canape" && canapeSub === "deploye") return "canape_deploye";
    if (mainChoice === "canape" && canapeSub === "plie") return "canape_plie";
    return null;
  };

  const submit = async () => {
    const choice = finalChoice();
    if (!choice || submitting) return;
    // Lien sécurisé : on enregistre la réponse directement sur la réservation.
    if (token) {
      setSubmitting(true);
      try {
        const r = await fetch(`${API}/api/rg/public/couchage/${token}`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ choice }),
        });
        if (!r.ok) { setSubmitting(false); return; }
        const data = await r.json();
        setDone({ summary: data.summary || buildSummary(choice), attached: true });
      } catch { setSubmitting(false); }
      return;
    }
    // Secours (URL statique) : résumé à envoyer à l'hôte.
    setDone({ summary: buildSummary(choice), attached: false });
  };

  if (loading) return (
    <div className="cs-page cs-loading">
      <Loader2 className="cs-spin" size={28} />
      <p>Chargement…</p>
    </div>
  );

  if (notFound) return (
    <div className="cs-page cs-loading" data-testid="cs-notfound">
      <p className="cs-title">Lien invalide</p>
      <p>Ce lien n&apos;est plus valide. Merci de contacter votre hôte.</p>
    </div>
  );

  if (done) {
    // Réponse rattachée (token) → simple remerciement, rien à renvoyer.
    if (done.attached) {
      return (
        <div className="cs-page cs-done" data-testid="cs-done">
          <div className="cs-done-icon"><Check size={40} /></div>
          <h1>Merci !</h1>
          <p>Votre choix de couchage est bien enregistré 🌿<br />Nous préparons tout avant votre arrivée.</p>
          <pre className="cs-summary" data-testid="cs-summary">{done.summary}</pre>
        </div>
      );
    }
    // Secours (sans token) → envoi du résumé à l'hôte par WhatsApp.
    let waHostUrl = null;
    if (adminPhone && done.summary) {
      const clean = String(adminPhone).replace(/[^\d+]/g, "").replace(/^\+?/, "");
      if (clean) waHostUrl = `https://wa.me/${clean}?text=${encodeURIComponent(done.summary)}`;
    }
    return (
      <div className="cs-page cs-done" data-testid="cs-done">
        <div className="cs-done-icon"><Check size={40} /></div>
        <h1>Merci !</h1>
        <p>Ton choix est enregistré. Dernière étape : envoie le résumé à ton hôte 🌿</p>
        <pre className="cs-summary" data-testid="cs-summary">{done.summary}</pre>
        {waHostUrl ? (
          <a
            href={waHostUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="cs-submit"
            style={{ marginTop: 22, background: "#25D366", textDecoration: "none" }}
            data-testid="cs-send-host"
          >
            📱 Envoyer à mon hôte via WhatsApp
          </a>
        ) : (
          <p className="cs-hint" style={{ marginTop: 22 }}>
            (numéro hôte non configuré — copie le résumé ci-dessus et envoie-le manuellement)
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="cs-page" data-testid="cs-survey">
      <div className="cs-leaf cs-leaf-tl" aria-hidden="true" />
      <div className="cs-leaf cs-leaf-tr" aria-hidden="true" />

      <header className="cs-header">
        <h1 className="cs-title">Choix du couchage</h1>
        <p className="cs-subtitle">pour le troisième voyageur <span className="cs-cursive">(enfant)</span></p>
        <div className="cs-divider"><span className="cs-leaflet">🌿</span></div>
      </header>

      <p className="cs-instruction">Vous avez <b>deux options</b> pour le couchage du troisième voyageur.</p>

      <div className="cs-options">
        <button
          type="button"
          className={`cs-option ${mainChoice === "lit_parapluie" ? "active" : ""}`}
          onClick={() => { setMainChoice("lit_parapluie"); setCanapeSub(null); }}
          data-testid="cs-opt-lit-parapluie"
        >
          <div className="cs-photo-wrap">
            <img src={IMG_LIT_PARAPLUIE} alt="Lit parapluie" className="cs-photo" />
            {mainChoice === "lit_parapluie" && <div className="cs-tick"><Check size={22} /></div>}
          </div>
          <div className="cs-option-label">LIT PARAPLUIE</div>
          <div className="cs-option-sub">Prêt à dormir</div>
        </button>

        <div className="cs-or">ou</div>

        <button
          type="button"
          className={`cs-option ${mainChoice === "canape" ? "active" : ""}`}
          onClick={() => setMainChoice("canape")}
          data-testid="cs-opt-canape"
        >
          <div className="cs-photo-wrap">
            <img src={IMG_CANAPE_DEPLOYE} alt="Canapé dépliant" className="cs-photo" />
            {mainChoice === "canape" && <div className="cs-tick"><Check size={22} /></div>}
          </div>
          <div className="cs-option-label">CANAPÉ DÉPLIANT</div>
          <div className="cs-option-sub">Choisissez ci-dessous</div>
        </button>
      </div>

      {mainChoice === "canape" && (
        <div className="cs-sub-block" data-testid="cs-sub-block">
          <p className="cs-sub-title">Comment le trouver à votre arrivée ?</p>
          <div className="cs-sub-options">
            <button
              type="button"
              className={`cs-sub-option ${canapeSub === "deploye" ? "active" : ""}`}
              onClick={() => setCanapeSub("deploye")}
              data-testid="cs-sub-deploye"
            >
              <div className="cs-photo-wrap">
                <img src={IMG_CANAPE_DEPLOYE} alt="Canapé déployé" className="cs-photo" />
                {canapeSub === "deploye" && <div className="cs-tick"><Check size={20} /></div>}
              </div>
              <div className="cs-sub-badge">DÉPLOYÉ</div>
              <div className="cs-sub-hint">Prêt à dormir</div>
            </button>
            <button
              type="button"
              className={`cs-sub-option ${canapeSub === "plie" ? "active" : ""}`}
              onClick={() => setCanapeSub("plie")}
              data-testid="cs-sub-plie"
            >
              <div className="cs-photo-wrap">
                <img src={IMG_CANAPE_PLIE} alt="Canapé plié" className="cs-photo" />
                {canapeSub === "plie" && <div className="cs-tick"><Check size={20} /></div>}
              </div>
              <div className="cs-sub-badge">PLIÉ</div>
              <div className="cs-sub-hint">À déplier vous-même</div>
            </button>
          </div>
        </div>
      )}

      <div className="cs-linge">
        <Check size={14} /> Linge de maison fourni
      </div>

      <button
        type="button"
        className="cs-submit"
        onClick={submit}
        disabled={!canValidate || submitting}
        data-testid="cs-submit"
      >
        {submitting ? "Envoi…" : "Valider mon choix"}
      </button>
    </div>
  );
}
